import type { RawAttribute } from '@manudev/model';
import type { BuildStore } from '../types';
import { orderedStoreys } from './storeys';
import type { BuildParams, BuildResult } from './types';

// A material name on elements that carry none, so they stop showing up in the audit and start
// counting in the takeoff. One IfcMaterial per name, shared by every element named here.
export async function setMaterial(store: BuildStore, params: BuildParams): Promise<BuildResult> {
  const name = (params.material ?? params.name ?? '').trim();
  if (!name) throw new Error('set_material needs a material name');
  const ids = params.ids ?? [];
  if (!ids.length) throw new Error('set_material needs ids');

  const elements = await store.elements();
  const known = new Set(elements.map((e) => e.expressID));
  const targets = ids.filter((id) => known.has(id));
  if (!targets.length) throw new Error(`none of those ids are in the model`);

  const storeys = await orderedStoreys(store);
  const first = elements.find((e) => e.expressID === targets[0]);
  const storey = storeys.find((s) => s.expressID === first?.storeyId) ?? storeys[0];
  if (!storey) throw new Error('the model has no storey');
  const anchor = await store.anchor(storey.expressID);
  const owner = anchor.ownerHistoryId != null ? `#${anchor.ownerHistoryId}` : null;

  // IFC2X3's IfcMaterial carries the name alone; IFC4 adds Description and Category.
  const attributes: RawAttribute[] =
    (anchor.schema ?? 'IFC4') === 'IFC2X3' ? [name] : [name, null, null];
  const existing = await store.materialId(name);
  const materialId =
    existing ?? (await store.addRaw('IfcMaterial', attributes, { product: false }));
  await store.addRaw(
    'IfcRelAssociatesMaterial',
    [await store.guid(), owner, null, null, targets.map((id) => `#${id}`), `#${materialId}`],
    { product: false },
  );

  return {
    created: [],
    removed: [],
    note: `${name} on ${targets.length} elements${existing ? '' : ', a new IfcMaterial'}`,
  };
}
